/**
 * 統一手續費模型（國泰證券，不折讓）— 前後端唯一來源。
 *
 * 為何集中：專案原本有三套各自的費率（apps/web/lib/fees.ts、components/ProfitCalc.tsx、
 * api/lib/trade-plans.ts），台股折數還不一致（ProfitCalc 打 0.28 折、其餘不折讓），導致
 * 「損益試算」與「模擬跟單組合」對同一檔算出的手續費對不起來。統一以**不折讓**為準。
 *
 * 費率（2026/06 國泰，標準費率不折讓）：
 *   台股：手續費率 0.1425%，買賣各課一次、單筆最低 NT$20；證交稅賣出 0.3%（ETF 0.1%）。
 *   美股網路單：手續費率 0.08%、免最低；賣出徵費（SEC/TAF 合併）約 0.00206%。
 * 帳戶幣別不換匯：台股以 TWD、美股以 USD 各自計算。
 * 註：費率可能調整、個人折讓不同；僅供「即時淨損益估算」，非帳務憑證。
 */
function isTaiwanSymbol(symbol) {
    return symbol.endsWith('.TW') || symbol.endsWith('.TWO');
}
/** 依 symbol（與是否 ETF）取得費率模型。台股 ETF 證交稅 0.1%，其餘一般 0.3%。 */
export function feeModelFor(symbol, isEtf = false) {
    if (isTaiwanSymbol(symbol)) {
        return { commissionRate: 0.001425, sellLevyRate: isEtf ? 0.001 : 0.003, minCommission: 20, currency: 'TWD' };
    }
    // 美股網路單：0.08% 免最低；賣出徵費（SEC/TAF）0.00206%。
    return { commissionRate: 0.0008, sellLevyRate: 0.0000206, minCommission: 0, currency: 'USD' };
}
/**
 * 單邊（買 or 賣）手續費 — 給「逐筆成交」記帳預估用（FIFO 分批會計）。
 * 買：手續費（含單筆最低）。賣：手續費（含單筆最低）+ 賣出稅費（台股證交稅 / 美股 SEC+TAF）。
 * 與 roundTripFees 同一套費率：roundTripFees.total === oneWayFee('buy',entry,..)+oneWayFee('sell',exit,..)。
 */
export function oneWayFee(side, price, shares, model) {
    const amt = Math.max(0, price) * Math.max(0, shares);
    if (amt <= 0)
        return 0;
    const commission = Math.max(amt * model.commissionRate, model.minCommission);
    const levy = side === 'sell' ? amt * model.sellLevyRate : 0;
    return commission + levy;
}
/**
 * 來回手續費明細（買賣各課手續費 + 賣出稅費），給「損益試算」拆項顯示與「模擬跟單」淨損益共用。
 * entry / exit 為價格，shares 為股數。opts.buyFeeIncluded / sellFeeIncluded 為 true 時該邊費視為 0
 * （已內含在價格，避免重複算；如富邦 API 匯入的含費成本）。
 */
export function roundTripFees(entry, exit, shares, model, opts) {
    const buyAmt = Math.max(0, entry) * Math.max(0, shares);
    const sellAmt = Math.max(0, exit) * Math.max(0, shares);
    const buyFee = opts?.buyFeeIncluded ? 0 : (buyAmt > 0 ? Math.max(buyAmt * model.commissionRate, model.minCommission) : 0);
    const sellFee = opts?.sellFeeIncluded ? 0 : (sellAmt > 0 ? Math.max(sellAmt * model.commissionRate, model.minCommission) : 0);
    const levy = opts?.sellFeeIncluded ? 0 : sellAmt * model.sellLevyRate;
    return { buyFee, sellFee, levy, total: buyFee + sellFee + levy };
}
/**
 * 估算「現在以 quote 賣出」的來回淨損益（含買賣手續費 + 賣出稅費）。需要股數才能算金額。
 * opts.buyFeeIncluded=true（如富邦含費成本）→ 買費不重估，賣費仍預估（符合富邦 unrealizedProfit 算法）。
 */
export function roundTripPnl(entry, quote, shares, model, opts) {
    if (!(entry > 0) || !(quote > 0) || !(shares > 0))
        return null;
    const entryValue = entry * shares;
    const exitValue = quote * shares;
    const f = roundTripFees(entry, quote, shares, model, opts);
    const grossPnl = exitValue - entryValue;
    const netPnl = grossPnl - f.total;
    return {
        grossPnl,
        fees: f.total,
        netPnl,
        netPnlPct: netPnl / entryValue,
        grossPnlPct: grossPnl / entryValue,
    };
}
/**
 * 沒有股數時的「淨損益率」估算：手續費按比例計，忽略最低手續費。
 * 美股網路單免最低，所以與股數無關、完全準確；台股忽略 NT$20 最低，量大時誤差可忽略。
 */
export function netPnlPctNoShares(entry, quote, model) {
    if (!(entry > 0) || !(quote > 0))
        return null;
    const gross = (quote - entry) / entry;
    const feePct = model.commissionRate + model.commissionRate + model.sellLevyRate; // 買 + 賣 + 賣出稅費
    return gross - feePct;
}
//# sourceMappingURL=fees.js.map